/* eslint-disable no-console */
import {
  chakra,
  forwardRef,
  HTMLChakraProps,
  omitThemingProps,
  StylesProvider,
  ThemingProps,
  useMultiStyleConfig,
  useStyles,
} from '@chakra-ui/system';
import { cx, __DEV__ } from '@chakra-ui/utils';
import * as React from 'react';
import { IRangeSliderContextValue, RangeSliderProvider } from './range-slider-context';
import {
  useRangeSliderInnerTrack,
  UseRangeSliderInnerTrackProps,
} from './use-range-slider-inner-track';
import { useRangeSliderRoot } from './use-range-slider-root';
import { IUseRangeSliderThumbProps, useRangeSliderThumb } from './use-range-slider-thumb';
import { useRangeSliderTrack } from './use-range-slider-track';

const RangeSliderContainer = forwardRef<HTMLChakraProps<'div'>, 'div'>((props, ref) => {
  const styles = useStyles();
  const { getRootProps } = useRangeSliderRoot();
  const rootProps = getRootProps(props);

  return (
    <chakra.div
      ref={ref}
      {...rootProps}
      __css={{
        display: 'inline-block',
        position: 'relative',
        ...styles.container,
      }}
      className="chakra-range-slider"
    >
      {props.children}
    </chakra.div>
  );
});

if (__DEV__) {
  RangeSliderContainer.displayName = 'RangeSliderContainer';
}

export type RangeSliderTrackProps = HTMLChakraProps<'div'>;

export const RangeSliderTrack = forwardRef<RangeSliderTrackProps, 'div'>((props, ref) => {
  const { getTrackProps } = useRangeSliderTrack();
  const trackProps = getTrackProps(props, ref);
  const styles = useStyles();

  return (
    <chakra.div
      {...trackProps}
      __css={{
        overflow: 'hidden',
        ...styles.track,
      }}
      className={cx('chakra-range-slider__track2', props.className)}
    />
  );
});

if (__DEV__) {
  RangeSliderTrack.displayName = 'RangeSliderTrack';
}

export interface IRangeSliderThumbProps
  extends Omit<HTMLChakraProps<'div'>, 'defaultValue'>,
    IUseRangeSliderThumbProps {}

/**
 * Slider component that acts as the handle used to select predefined
 * values by dragging its handle along the track
 */
export const RangeSliderThumb = forwardRef<IRangeSliderThumbProps, 'div'>(
  (
    {
      index: thumbKey,
      min,
      max,
      value,
      defaultValue,
      onValueChangeStart,
      onValueChangeEnd,
      onValueChange,
      id,
      name,
      getAriaValueText,
      'aria-label': ariaLabel,
      'aria-valuetext': ariaValueText,
      'aria-labelledby': ariaLabelledby,
      ...props
    },
    ref
  ) => {
    const { getThumbProps, getInputProps } = useRangeSliderThumb({
      index: thumbKey,
      min,
      max,
      value,
      defaultValue,
      onValueChangeStart,
      onValueChangeEnd,
      onValueChange,
      id,
      name,
      getAriaValueText,
      'aria-label': ariaLabel,
      'aria-valuetext': ariaValueText,
      'aria-labelledby': ariaLabelledby,
    });

    const thumbProps = getThumbProps(props, ref);
    const inputProps = getInputProps(props, ref);
    const styles = useStyles();

    return (
      <React.Fragment>
        <chakra.div
          {...thumbProps}
          __css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 0,
            cursor: 'pointer',
            ...styles.thumb,
            _hover: {
              transform: `translateX(-50%) translateY(-50%) scale(1.15) !important`,
            },
          }}
          className={cx('chakra-range-slider__thumb', props.className)}
        />
        <input {...inputProps} />
      </React.Fragment>
    );
  }
);

if (__DEV__) {
  RangeSliderThumb.displayName = 'RangeSliderThumb';
}

export interface IRangeSliderInnerTrackProps
  extends HTMLChakraProps<'div'>,
    UseRangeSliderInnerTrackProps {}

export const RangeSliderFilledTrack = forwardRef<IRangeSliderInnerTrackProps, 'div'>(
  ({ startThumbKey, endThumbKey, ...props }, ref) => {
    const { getInnerTrackProps } = useRangeSliderInnerTrack({
      startThumbKey,
      endThumbKey,
    });

    const styles = useStyles();

    const trackStyles = {
      width: 'inherit',
      height: 'inherit',
      ...styles.filledTrack,
    };

    const trackProps = getInnerTrackProps(props, ref);

    return (
      <chakra.div {...trackProps} __css={trackStyles} className="chakra-slider__filled-track" />
    );
  }
);

if (__DEV__) {
  RangeSliderFilledTrack.displayName = 'RangeSliderFilledTrack';
}

export interface IRangeSliderProps
  extends Partial<Omit<IRangeSliderContextValue, 'setThumbValue' | 'values'>>,
    ThemingProps<'Slider'>,
    HTMLChakraProps<'div'> {}

export const RangeSlider = forwardRef<IRangeSliderProps, 'div'>((props, ref) => {
  const styles = useMultiStyleConfig('Slider', props);
  const { min, max, orientation, step, isDisabled, isReadOnly, isReversed, ...otherProps } = props;
  const containerProps = omitThemingProps(otherProps);
  const { children, ...containerPropsWithoutChildren } = containerProps;
  const count: number = React.Children.count(children) || 2;
  const sliders = [];

  for (let i = 0; i < count; i++) {
    if (i % 2 === 0) {
      sliders.push(i);
    }
  }

  return (
    <RangeSliderProvider
      isDisabled={isDisabled ?? false}
      isReadOnly={isReadOnly ?? false}
      isReversed={isReversed ?? false}
      max={max ?? 100}
      min={min ?? 0}
      orientation={orientation ?? 'horizontal'}
      step={step ?? 1}
    >
      <StylesProvider value={styles}>
        <RangeSliderContainer ref={ref} {...containerPropsWithoutChildren}>
          <RangeSliderTrack>
            {sliders.map((i) => {
              return <RangeSliderFilledTrack endThumbKey={i + 1} key={i} startThumbKey={i} />;
            })}
          </RangeSliderTrack>
          {children}
        </RangeSliderContainer>
      </StylesProvider>
    </RangeSliderProvider>
  );
});

RangeSlider.defaultProps = {
  orientation: 'horizontal',
};

if (__DEV__) {
  RangeSlider.displayName = 'RangeSlider';
}
